// Production-variant analog of `url-element-mixin.js`. Same `optionBundles`,
// branding type, and option overrides so the resolver returns identical
// values for the same persisted options. Reuses `TextElementRenderMixin`
// for font/spacing/text projection (it already drives the `iText` /
// `iLink` style overrides). Adds the URL-only computeds (href resolution,
// border + background overrides). Text comes from `TextOption`, falling back
// to legacy `HrefAction.label` for links authored before the move.

import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import { suppressesNavigation } from "%/dynamic-component-editor/helpers/builder-navigation-guard";
import {
  TextElementRenderMixin,
} from "%/dynamic-component-editor/standard-elements/text-element/text-element-render-mixin";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import BasicTextOptions from "%/dynamic-component-editor/bundles/basic-text-options";
import TextOptions from "%/dynamic-component-editor/bundles/text-options";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import BorderOptions from "%/dynamic-component-editor/bundles/border-options";
import BackgroundOptions from "%/dynamic-component-editor/bundles/background-options";
import BrandedTextStyleOptions from "%/dynamic-component-editor/bundles/branded-text-style-options";
import EffectsOptions from "%/dynamic-component-editor/bundles/effects-options";
import HrefOption from "%/dynamic-component-editor/options/href-option";
import HrefAction from "%/dynamic-component-editor/options/href-action";
import TextOption from "%/dynamic-component-editor/options/text-option";
import { resolveLinkText } from "%/dynamic-component-editor/helpers/link-text-migration";
import Label from "%/dynamic-component-editor/options/label";
import LinkType from "%/dynamic-component-editor/options/link-type";
import { resolveContextualPageValue } from "%/dynamic-components/render-engine/options/click-actions/contextual-page-value";
import { isBuilderQuiet, pageLinkValueFor } from "%/dynamic-component-editor/standard-elements/click-actions/resolve-page-target";
import { hasUnfilledPlaceholder } from "%/dynamic-component-editor/standard-elements/click-actions/resolve-context-href";
import BorderRadius from "%/dynamic-component-editor/options/border-radius";
import BorderSides from "%/dynamic-component-editor/options/border-sides";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import BackgroundGradient from "%/dynamic-component-editor/options/background-gradient";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import TypographyBranding from "%/dynamic-component-editor/classes/branding/typography-branding";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";

export const UrlElementRenderMixin = {
  brandingType: TypographyBranding,
  mixins: [DynamicElementMixin, TextElementRenderMixin],
  props: {
    href: {
      type: String,
      required: false,
      default: "",
    },
  },
  optionsOrder: [
    "link-options",
  ],
  optionBundles: [
    new AppearanceOptions({}, {
      "border-radius": false,
    }),
    new BrandedTextStyleOptions({
      "default.text-branding-style-option": "paragraph",
    }),
    BasicTextOptions,
    TextOptions,
    SpacingOptions,
    new LayoutWidthHeightOptions({
      "default.layout-width": "hug",
      "default.layout-height": "hug",
    }, {
      "layout-height": true,
      "max-height": true,
      "min-height": true,
    }),
    BorderOptions,
    new BackgroundOptions({}, {
      "background-image": true,
      "background-gradient": true,
    }),
    EffectsOptions,
  ],
  options: [
    new HrefOption(undefined, true),
    new Label(undefined, true),
    new LinkType(undefined, true),
  ],
  computed: {
    builderNoNavHref() {
      if (suppressesNavigation(this)) {
        return null;
      }
      return this.href || this.computedHref;
    },
    target() {
      return this.getOptionValue(HrefAction)?.openInNewTab ? "_blank" : "_self";
    },
    rel() {
      return "";
    },
    myBorderRadius() {
      return this.getOptionValue(BorderRadius);
    },
    myBorderSides() {
      return this.getOptionValue(BorderSides);
    },
    urlStyleOverrides() {
      const overrides = {
        backgroundColor: this.getOptionValue(BackgroundColor),
        hoverBackgroundColor: this.getOptionValue(BackgroundColor, BaseOption.$states.HOVER),
      };
      if (this.shouldShowLink) {
        overrides.borderRadius = this.myBorderRadius;
        overrides.backgroundGradient = this.getOptionValue(BackgroundGradient);
        overrides.backgroundGradientHover = this.getOptionValue(BackgroundGradient, BaseOption.$states.HOVER);
      }
      return overrides;
    },
    myStyleOverrides() {
      return { ...this.textStyleOverrides, ...this.urlStyleOverrides };
    },
    computedHref() {
      const linkValues = this.getOptionValue(HrefAction);
      if (!linkValues) {
        return "";
      }
      const urlType = linkValues.linkType;
      let urlValue = linkValues.value;

      if (typeof urlValue === "string" && urlValue.startsWith("http")) {
        return urlValue;
      } else if (typeof urlValue === "object") {
        urlValue = urlValue.value;
      }

      switch (urlType) {
        case LinkType.LINK_TYPE_HYPERLINK:
          return urlValue;
        case LinkType.LINK_TYPE_PAGE: {
          urlValue = pageLinkValueFor(linkValues, urlValue, this.scopedProvidedContexts, { quiet: isBuilderQuiet(this) });
          if (urlValue === null) {
            return;
          }
          const contextHref = this.contextResolvedHref(linkValues, urlValue, this.links.web);
          if (contextHref) {
            return contextHref;
          }
          const resolved = resolveContextualPageValue(urlValue);
          if (resolved === null || resolved === undefined) {
            return;
          }
          const stripped = resolved.startsWith("/") ? resolved.substring(1) : resolved;
          const pageHref = `${this.links.web}/${stripped}`;
          return hasUnfilledPlaceholder(pageHref) ? undefined : pageHref;
        }
        case LinkType.LINK_TYPE_STORY:
          return `${this.links.web}/story/${urlValue}`;
        case LinkType.LINK_TYPE_HASHTAG:
          return `${this.links.web}/stories/${urlValue}`;
        default:
          throw new DynamicComponentError("Url type not found", {
            urlType,
            urlValue,
          });
      }
    },
    shouldShowLink() {
      return true;
    },
    textValueOptionClass() {
      return TextOption;
    },
    textValueKey() {
      return undefined;
    },
    label() {
      return resolveLinkText(this.renderedText, this.getOptionValue(HrefAction));
    },
    hasLabel() {
      return typeof this.label === "string" && this.label.length > 0;
    },
  },
};
